import { BorderRadiuses, FontFamilies } from 'core/CssVariables';
import styled, { css, ThemeProps } from 'styled-components';
import { Row } from 'components/Grid/Grid';
import { TThemeProps } from 'theme/theme';

const SGaugesMenuItemWrapper = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  background-color: ${props => props.theme.secondaryBackgroundColor};
  border-radius: ${BorderRadiuses.RadiusLG}px;
  margin-top: 13px;
  padding: 12px 16px;
  width: fit-content;
  box-shadow: 0px 4px 12px
    ${({ theme }: ThemeProps<TThemeProps>) => theme.dropdown.boxShadowColor};
`;

const SMenuDotWrapper = styled.div`
  svg {
    path {
      fill: ${props => props.theme.color};
    }
  }
`;

// Dashbar gauges row. In the header ($fitOneRow) the row never wraps and grows
// to fill the space left of the right-hand controls; each gauge column may then
// shrink below its natural width so all seven (plus the optional reset icon)
// stay on a single line at any width down to 1280px, with no per-breakpoint
// tuning (ZEST-735). Elsewhere (mobile dropdown) it keeps the default wrapping
// grid.
const SGaugesRow = styled(Row)<{ $fitOneRow?: boolean }>`
  ${({ $fitOneRow }) =>
    $fitOneRow &&
    css`
      flex: 1;
      min-width: 0;
      flex-wrap: nowrap;

      /* antd's gutter applies a -6px inline side margin (paired with 6px column
         padding) so the outer edges normally cancel out. Here the row grows to
         sit flush against the right-hand controls, so that negative margin would
         push it ~6px past its container and into those controls. Zero it out and
         let the columns' own padding provide the edge spacing instead. */
      margin-left: 0 !important;
      margin-right: 0 !important;

      & > .ant-col {
        min-width: 0;
      }
    `}
`;

/* ZEST-757 dashbar gauge: a labelled figure over a 3px severity bar, replacing
   the coloured circle. The bar carries the state the circle's fill used to, so
   severity still reads at a glance without relying on colour alone. */
const SGaugeBlock = styled.div<{ $fitOneRow?: boolean; $clickable?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  /* Tiles carry one or two lines, so they fill the row's height and the bar is
     pushed to the bottom — otherwise each tile's bar sits at its own baseline
     and the row reads as ragged. */
  height: 100%;
  /* If a tile is ever squeezed below its content, clip rather than let the text
     spill into the neighbouring tile. */
  overflow: hidden;
  user-select: none;
  font-family: ${FontFamilies.FontShell};
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};

  ${({ $fitOneRow }) =>
    $fitOneRow &&
    css`
      min-width: 0;
    `}
`;

const SGaugeLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${props => props.theme.dashbar.labelColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SGaugeFigure = styled.div`
  display: flex;
  align-items: baseline;
  /* design: expanded gauge Container(margin) itemSpacing = 4 */
  gap: 4px;
  white-space: nowrap;
`;

const SGaugeValue = styled.span<{ $color?: string }>`
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  font-variant-numeric: tabular-nums;
  color: ${props => props.$color || props.theme.dashbar.valueColor};
`;

const SGaugeSub = styled.span<{ $color?: string }>`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: ${props => props.$color || props.theme.dashbar.labelColor};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* One half of the merged ACTIVE TICKETS gauge. Each half is its own click
   target so Open and New keep the drill-downs they had as separate tiles. */
const SGaugeSegment = styled.span<{ $clickable?: boolean }>`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};

  &:hover {
    text-decoration: ${props => (props.$clickable ? 'underline' : 'none')};
    text-underline-offset: 2px;
  }
`;

/* "Label: value" on one line. New and FR SLA Failed are two of these stacked
   inside a single segment, so they stay one column and one click target. */
const SGaugeSegmentLine = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SGaugeBar = styled.div<{ $color?: string }>`
  height: 3px;
  border-radius: 3px;
  /* auto keeps every bar on one baseline regardless of how many lines a tile
     has. No padding here: box-sizing is border-box globally, so padding eats
     the 3px and the fill inside (height:100%) collapses to nothing. */
  margin-top: auto;
  background-color: ${props => props.theme.dashbar.trackColor};

  i {
    display: block;
    height: 100%;
    width: 100%;
    border-radius: 3px;
    background-color: ${props => props.$color || props.theme.dashbar.infoColor};
  }
`;

const SGaugeCollapsedRow = styled.div`
  display: flex;
  align-items: center;
  /* design: collapsed gauge Container(margin) itemSpacing = 6 */
  gap: 6px;

  svg {
    flex: 0 0 auto;
  }

  .gauge-icon {
    color: ${props => props.theme.dashbar.valueColor};
  }
`;

export {
  SGaugesMenuItemWrapper,
  SMenuDotWrapper,
  SGaugesRow,
  SGaugeBlock,
  SGaugeLabel,
  SGaugeFigure,
  SGaugeValue,
  SGaugeSub,
  SGaugeSegment,
  SGaugeSegmentLine,
  SGaugeBar,
  SGaugeCollapsedRow,
};
